import React, { useContext } from "react";
import styled from "styled-components";
import IconWithFallback from "Global/IconWithFallback";
import { ICON_TYPES } from "@shared/common/constants";
import { ConsolidatedCoinDataWithKey } from "@shared/types";
import { RootState } from "app/store";
import { PrecisionConfig } from "types";
import { useIsSubUser, useSelectUser } from "@shared/utils/hooks/misc.ts";
import { DrawerContext } from "common/Drawers/Drawer.context.ts";
import {
  getNewLookTokens,
  NEW_LOOK_RADIUS,
  newLookType,
} from "common/NewLook/tokens.ts";
import CoinActionsDropdown from "features/portfolio/common/CoinActionsDropdown.tsx";
import useAssetItem from "features/portfolio/common/useAssetItem.tsx";

const ROW_PADDING_LEFT = 14;
const ROW_PADDING_RIGHT = 8;
const ACTIONS_WIDTH = 36;

/** Caption padding that puts the sorters over the row's three columns. */
export const NEW_LOOK_COIN_ROW_SORTERS_PADDING = `4px ${
  ROW_PADDING_RIGHT + ACTIONS_WIDTH + 1
}px 4px ${ROW_PADDING_LEFT + 1}px`;

interface NewLookCoinCardProps {
  coinData: ConsolidatedCoinDataWithKey;
  precisionSelector: (state: RootState) => PrecisionConfig | undefined;
}

/**
 * A coin as one phone row: the coin and its price, what it is worth and the
 * quantity, then its 24h line and change.
 */
const NewLookCoinCard = ({
  coinData,
  precisionSelector,
}: NewLookCoinCardProps) => {
  const { isDrawer } = useContext(DrawerContext) || {};
  const {
    getQuantity,
    getPrice,
    getNewLookPriceTrend,
    getTotalAmount,
    openCoinExtraDetails,
  } = useAssetItem({ precisionSelector });
  const isSubUser = useIsSubUser();
  const user = useSelectUser();
  const open = () => openCoinExtraDetails(coinData);

  return (
    <Row
      role="button"
      tabIndex={0}
      onClick={open}
      onKeyDown={(event) => {
        if (event.key === "Enter" && event.target === event.currentTarget)
          open();
      }}
    >
      <Identity>
        <IconWithFallback
          iconType={ICON_TYPES.COIN}
          coinOrExchangeName={coinData.symbol}
          pxIconSize={28}
        />
        <Stack>
          <Name>{coinData.symbol}</Name>
          <Meta>{getPrice(coinData)}</Meta>
        </Stack>
      </Identity>
      <Stack $end={true}>
        <Figure>{getTotalAmount(coinData)}</Figure>
        <Meta>{getQuantity(coinData.quantity, coinData.symbol)}</Meta>
      </Stack>
      <Trend>{getNewLookPriceTrend(coinData, "stacked")}</Trend>
      <Actions>
        {!isDrawer && !isSubUser && !user?.isWhopExperience() && (
          <CoinActionsDropdown coinData={coinData} />
        )}
      </Actions>
    </Row>
  );
};

export default React.memo(NewLookCoinCard);

const Row = styled.div`
  display: grid;
  grid-template-columns:
    minmax(0, 9fr) minmax(0, 8fr) minmax(0, 7fr)
    ${ACTIONS_WIDTH}px;
  align-items: center;
  min-height: 64px;
  padding: 10px ${ROW_PADDING_RIGHT}px 10px ${ROW_PADDING_LEFT}px;
  background: ${(p) => getNewLookTokens(p.theme).panel};
  border: ${(p) => p.theme.borders.panel};
  border-radius: ${NEW_LOOK_RADIUS.panel}px;
  box-shadow: ${(p) => p.theme.shadows.panel};
  cursor: pointer;
  outline: none;
  &:active {
    background: ${(p) => getNewLookTokens(p.theme).rowHover};
  }
  &:focus-visible {
    box-shadow: 0 0 0 2px ${(p) => getNewLookTokens(p.theme).focus};
  }
`;
const Identity = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
  padding-right: 8px;
`;
const Stack = styled.div<{ $end?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: ${(p) => (p.$end ? "flex-end" : "flex-start")};
  gap: 2px;
  min-width: 0;
`;
const Name = styled.span`
  ${newLookType.panelTitle};
  color: ${(p) => getNewLookTokens(p.theme).ink};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
`;
const Figure = styled.span`
  ${newLookType.figure};
  font-size: 14px;
  color: ${(p) => getNewLookTokens(p.theme).ink};
  white-space: nowrap;
  * {
    font-size: inherit !important;
    font-weight: inherit !important;
    color: inherit !important;
  }
`;
const Meta = styled.span`
  ${newLookType.meta};
  color: ${(p) => getNewLookTokens(p.theme).muted};
  white-space: nowrap;
  * {
    font-size: 12px !important;
  }
`;
const Trend = styled.div`
  display: flex;
  justify-content: flex-end;
  min-width: 0;
  padding-left: 8px;
`;
const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
`;
